package com.siriusxm.pia.views.chat

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.Badge
import com.siriusxm.pia.components.BadgeColor
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.svgIcon
import com.siriusxm.unifiedcontent.cmc.ChatNotFound
import com.siriusxm.unifiedcontent.cmc.ChatStatus
import com.siriusxm.unifiedcontent.cmc.ChatTranscript
import com.siriusxm.unifiedcontent.cmc.ChatTranscriptListItem
import com.siriusxm.unifiedcontent.cmc.TranscriptMessage
import kotlinx.coroutines.CancellationException
import smithy4kt.SmithyClientError
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Admin review screen for full chat transcripts (UCA-1091).
 *
 * Reached only through the unlisted `#chat/transcripts` URL — there is no link to it in the console
 * chrome. Access is gated server-side by the unified-content AVP action
 * `SXM::UnifiedContentManagementConsole_ViewChatTranscripts`: the two API operations behind this
 * screen (`listChatTranscripts` / `getChatTranscript`) check the caller's token and return 403 when
 * the caller lacks the grant. This composable therefore does no client-side entitlement check of its
 * own — the route is a UI affordance, not the authorization boundary — and instead renders a plain
 * "not authorized" state when the API refuses. Doing it this way is deliberate: the AVP grant, not a
 * Cognito group, decides who may read transcripts, and only the server knows the AVP answer.
 *
 * Two shapes share this one entry point, distinguished by whether the URL names a conversation:
 * - `#chat/transcripts`            → [TranscriptListView], the index of recent conversations
 * - `#chat/transcripts/{id}`       → [TranscriptDetailView], one conversation's complete trace
 *
 * @param app the ChatApplication providing the CMC service client and navigation
 * @param conversationId the conversation to show in detail, or null for the list
 */
@Composable
fun TranscriptsScreen(app: ChatApplication, conversationId: String?) {
    // The markdown rules assistant bubbles rely on are reused for assistant turns in the detail view.
    Style(ChatStyles)

    Div({
        style {
            flex(1, 1)
            minWidth(0.px)
            height(100.percent)
            overflowY("auto")
            backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
        }
    }) {
        if (conversationId == null) {
            TranscriptListView(app)
        } else {
            TranscriptDetailView(app, conversationId)
        }
    }
}

/**
 * The transcript index: a table of recent conversations across all users, most recent first.
 *
 * Rows come from `listChatTranscripts`, which reads the DynamoDB conversations table (the cheap 7-day
 * index). S3 retains detail for 30 days, so an older transcript remains directly addressable by id
 * after it drops out of this list. Selecting a row navigates to `#chat/transcripts/{id}`; the URL is
 * the single source of truth for which screen is shown, matching the rest of the chat module.
 */
@Composable
private fun TranscriptListView(app: ChatApplication) {
    var items by remember { mutableStateOf<List<ChatTranscriptListItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorState by remember { mutableStateOf<LoadError?>(null) }

    LaunchedEffect(Unit) {
        isLoading = true
        errorState = null
        try {
            items = app.cmcService.chatTranscripts.listChatTranscripts(limit = 100).transcripts
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            errorState = classifyError(e)
        } finally {
            isLoading = false
        }
    }

    Div({ style { padding(24.px); maxWidth(1100.px); property("margin", "0 auto") } }) {
        // Header: title + link back to the live chat.
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(20.px)
            }
        }) {
            Div {
                H2({ style { margin(0.px); fontSize(22.px); color(SXMUI.textColor.value()) } }) {
                    Text("Chat Transcripts")
                }
                Div({ style { marginTop(4.px); fontSize(13.px); color(SXMUI.subtleTextColor.value()) } }) {
                    Text("Full agentic traces — every tool call and result — for admin review.")
                }
            }
            BackLink("Back to chat") { app.context.navigate("chat") }
        }

        when {
            isLoading -> InfoPanel("Loading transcripts…")
            errorState != null -> ErrorPanel(errorState!!)
            items.isEmpty() -> InfoPanel("No conversations found.")
            else -> TranscriptTable(items) { id -> app.context.navigate("chat/transcripts/$id") }
        }
    }
}

/**
 * Renders the transcript summaries as a compact, read-only table.
 *
 * Rows retain the server's newest-first order. The entire row is selectable so the caller can route
 * to that conversation's detail trace without putting a second action control in every row.
 */
@Composable
private fun TranscriptTable(items: List<ChatTranscriptListItem>, onSelect: (String) -> Unit) {
    Div({
        style {
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            borderRadius(8.px)
            overflow("hidden")
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
        }
    }) {
        Table({
            style {
                width(100.percent)
                property("border-collapse", "collapse")
                fontSize(13.px)
            }
        }) {
            Thead {
                Tr {
                    listOf("Status", "Preview", "User", "Created").forEach { col ->
                        Th({
                            style {
                                property("text-align", "left")
                                padding(10.px, 14.px)
                                color(SXMUI.subtleTextColor.value())
                                fontWeight(600)
                                property("border-bottom", "1px solid ${SXMUI.borderDividerDefault.value()}")
                                property("white-space", "nowrap")
                            }
                        }) { Text(col) }
                    }
                }
            }
            Tbody {
                items.forEach { item ->
                    TranscriptRow(item) { onSelect(item.conversationId) }
                }
            }
        }
    }
}

/**
 * Renders one conversation summary with status, first-user-message preview, owner, and creation time.
 * The whole row is a click target into the detail view; all text is emitted through Compose DOM so
 * conversation content is escaped rather than interpreted as markup.
 */
@Composable
private fun TranscriptRow(item: ChatTranscriptListItem, onClick: () -> Unit) {
    Tr({
        style {
            cursor("pointer")
            property("border-bottom", "1px solid ${SXMUI.borderDividerDefault.value()}")
        }
        onClick { onClick() }
    }) {
        Td({ style { padding(10.px, 14.px); property("white-space", "nowrap") } }) {
            StatusBadge(item.status)
        }
        Td({
            style {
                padding(10.px, 14.px)
                color(SXMUI.textColor.value())
                maxWidth(480.px)
                overflow("hidden")
                property("text-overflow", "ellipsis")
                property("white-space", "nowrap")
            }
        }) {
            Text(item.preview ?: "(no preview)")
        }
        Td({
            style {
                padding(10.px, 14.px)
                color(SXMUI.subtleTextColor.value())
                fontSize(12.px)
                property("white-space", "nowrap")
            }
        }) {
            Text(item.userId?.takeIf { it.isNotBlank() } ?: "—")
        }
        Td({
            style {
                padding(10.px, 14.px)
                color(SXMUI.subtleTextColor.value())
                fontSize(12.px)
                property("white-space", "nowrap")
            }
        }) {
            Text(formatTimestamp(item.createdAt))
        }
    }
}

/**
 * One conversation's complete captured trace.
 *
 * The message list is whatever the server returned: the S3 blob's verbatim history when it exists, or
 * the Dynamo record's slimmer message list as a fallback when no blob was captured (still processing,
 * or capture failed — it is best-effort in the worker). Either way each turn is rendered by
 * its role, with assistant tool-call turns and tool-result turns shown explicitly — the whole point of
 * this screen over the user-facing chat view.
 */
@Composable
private fun TranscriptDetailView(app: ChatApplication, conversationId: String) {
    var transcript by remember { mutableStateOf<ChatTranscript?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var errorState by remember { mutableStateOf<LoadError?>(null) }

    LaunchedEffect(conversationId) {
        isLoading = true
        errorState = null
        transcript = null
        try {
            transcript = app.cmcService.chatTranscripts.getChatTranscript(conversationId)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            errorState = classifyError(e)
        } finally {
            isLoading = false
        }
    }

    Div({ style { padding(24.px); maxWidth(900.px); property("margin", "0 auto") } }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            H2({ style { margin(0.px); fontSize(20.px); color(SXMUI.textColor.value()) } }) {
                Text("Transcript")
            }
            BackLink("Back to list") { app.context.navigate("chat/transcripts") }
        }

        when {
            isLoading -> InfoPanel("Loading transcript…")
            errorState != null -> ErrorPanel(errorState!!)
            transcript != null -> {
                TranscriptMeta(transcript!!)
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(12.px)
                        marginTop(16.px)
                    }
                }) {
                    transcript!!.messages.forEachIndexed { index, msg ->
                        key(index) { TranscriptMessageCard(msg) }
                    }
                    if (transcript!!.messages.isEmpty()) {
                        InfoPanel("This transcript has no captured messages.")
                    }
                }
            }
        }
    }
}

/**
 * Renders the read-only metadata header for one transcript: conversation id, user, status, creation
 * time, and capture time. Fields wrap on narrow screens while identifiers may break at any character.
 */
@Composable
private fun TranscriptMeta(transcript: ChatTranscript) {
    Div({
        style {
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            borderRadius(8.px)
            padding(14.px, 16.px)
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
            display(DisplayStyle.Flex)
            flexWrap(FlexWrap.Wrap)
            gap(20.px)
            fontSize(12.px)
        }
    }) {
        MetaField("Conversation", transcript.conversationId)
        MetaField("User", transcript.userId?.takeIf { it.isNotBlank() } ?: "—")
        transcript.status?.let {
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(3.px) } }) {
                Span({ style { color(SXMUI.subtleTextColor.value()); fontWeight(600) } }) { Text("Status") }
                StatusBadge(it)
            }
        }
        MetaField("Created", formatTimestamp(transcript.createdAt))
        MetaField("Captured", formatTimestamp(transcript.capturedAt))
    }
}

/** Renders one label/value pair in the transcript metadata header. */
@Composable
private fun MetaField(label: String, value: String) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(3.px) } }) {
        Span({ style { color(SXMUI.subtleTextColor.value()); fontWeight(600) } }) { Text(label) }
        Span({ style { color(SXMUI.textColor.value()); property("word-break", "break-all") } }) { Text(value) }
    }
}

/**
 * A single message turn in the detail trace.
 *
 * The card's accent and label are keyed off the role string the server sends ("system", "user",
 * "assistant", "tool"). Assistant prose is rendered as markdown (reusing [MarkdownContent] and its
 * `.chat-markdown` styles) so tables and code render as they do in the live chat; every other role's
 * content is shown as preformatted text, because tool arguments and tool results are JSON payloads
 * that must not be reflowed. Assistant tool-use turns additionally list each requested call with its
 * raw JSON arguments; tool-result turns are headed by the tool they answer.
 */
@Composable
private fun TranscriptMessageCard(msg: TranscriptMessage) {
    val role = msg.role.lowercase()
    val accent = when (role) {
        "user" -> SXMUI.buttonBackgroundPrimary.value()
        "assistant" -> SXMUI.statusSuccessColor.value()
        "tool" -> SXMUI.toolAccentColor.value()
        else -> SXMUI.subtleTextColor.value() // system / unknown
    }

    Div({
        style {
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            property("border-left", "3px solid $accent")
            borderRadius(8.px)
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
            padding(12.px, 14.px)
        }
    }) {
        // Role header, plus the tool name on a tool-result turn.
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(8.px)
            }
        }) {
            Span({
                style {
                    color(accent)
                    fontWeight(700)
                    fontSize(12.px)
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.04em")
                }
            }) { Text(role) }
            if (role == "tool" && !msg.toolName.isNullOrBlank()) {
                Span({ style { color(SXMUI.subtleTextColor.value()); fontSize(12.px) } }) {
                    Text("· ${msg.toolName}")
                }
            }
        }

        // Content. Assistant prose is markdown; everything else is preformatted so JSON keeps shape.
        val content = msg.content
        if (!content.isNullOrBlank()) {
            if (role == "assistant") {
                MarkdownContent(content)
            } else {
                PreformattedBlock(content)
            }
        } else if (role == "assistant" && !msg.toolCalls.isNullOrEmpty()) {
            // Assistant turn that is only tool calls, no prose.
            Span({ style { color(SXMUI.subtleTextColor.value()); fontSize(12.px); fontStyle("italic") } }) {
                Text("(no text — tool call only)")
            }
        }

        // Tool calls requested on this assistant turn.
        msg.toolCalls?.takeIf { it.isNotEmpty() }?.let { calls ->
            Div({ style { marginTop(10.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
                calls.forEach { call ->
                    Div {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(6.px)
                                marginBottom(4.px)
                            }
                        }) {
                            svgIcon(SVGIcons.tool) { size = 14.px }
                            Span({ style { fontWeight(600); fontSize(12.px); color(SXMUI.textColor.value()) } }) {
                                Text(call.name)
                            }
                        }
                        call.arguments?.takeIf { it.isNotBlank() && it != "{}" }?.let { args ->
                            PreformattedBlock(prettyJson(args))
                        }
                    }
                }
            }
        }
    }
}

/**
 * Renders JSON payloads and non-markdown message content as escaped, monospace text. Long lines can
 * scroll horizontally while ordinary prose wraps, keeping raw tool data readable without executing
 * or interpreting any of its contents.
 */
@Composable
private fun PreformattedBlock(text: String) {
    Pre({
        style {
            margin(0.px)
            padding(10.px, 12.px)
            borderRadius(6.px)
            backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            overflowX("auto")
            fontSize(12.px)
            property("font-family", "monospace")
            property("white-space", "pre-wrap")
            property("word-break", "break-word")
            color(SXMUI.textColor.value())
        }
    }) { Text(text) }
}

/**
 * Renders a conversation status with the shared [Badge] component so transcript state uses the same
 * spacing, typography, borders, and semantic colors as badges elsewhere in CMC. Unknown future enum
 * values fall back to a neutral badge and an explicit label so the UI remains forward-compatible.
 */
@Composable
private fun StatusBadge(status: ChatStatus) {
    val (label, color) = when (status) {
        ChatStatus.COMPLETED -> "Completed" to BadgeColor.GREEN
        ChatStatus.FAILED -> "Failed" to BadgeColor.RED
        ChatStatus.PROCESSING -> "Processing" to BadgeColor.BLUE
        ChatStatus.PENDING -> "Pending" to BadgeColor.YELLOW
        else -> "Unknown" to BadgeColor.GRAY
    }
    Badge(text = label, color = color)
}

/**
 * Renders a compact back-navigation affordance with a shared chevron icon. The caller owns routing so
 * the same component can return from either transcript detail or the transcript list.
 */
@Composable
private fun BackLink(label: String, onClick: () -> Unit) {
    Span({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            cursor("pointer")
            color(SXMUI.brandColor.value())
            fontSize(13.px)
        }
        onClick { onClick() }
    }) {
        svgIcon(SVGIcons.chevronLeft) { size = 16.px }
        Text(label)
    }
}

/**
 * Renders a neutral centered panel for non-error states such as loading, an empty list, or an empty
 * transcript. It intentionally uses shared secondary text styling so it does not compete with data.
 */
@Composable
private fun InfoPanel(message: String) {
    Div({
        style {
            padding(24.px)
            color(SXMUI.subtleTextColor.value())
            fontSize(14.px)
            property("text-align", "center")
        }
    }) { Text(message) }
}

/** Renders a classified load error, with an access-denied case distinct from a generic failure. */
@Composable
private fun ErrorPanel(error: LoadError) {
    Div({
        style {
            padding(20.px)
            borderRadius(8.px)
            backgroundColor(SXMUI.messageAlertBackground.value())
            border(1.px, LineStyle.Solid, SXMUI.messageAlertBorder.value())
            color(SXMUI.messageAlertForeground.value())
            fontSize(14.px)
        }
    }) {
        when (error) {
            LoadError.Forbidden -> {
                Div({ style { fontWeight(700); marginBottom(4.px) } }) { Text("Not authorized") }
                Text("You don't have permission to view transcripts.")
            }
            LoadError.NotFound -> {
                Div({ style { fontWeight(700); marginBottom(4.px) } }) { Text("Transcript not found") }
                Text("This conversation has no record, or its transcript has expired.")
            }
            LoadError.Unexpected -> {
                Div({ style { fontWeight(700); marginBottom(4.px) } }) { Text("Couldn't load transcripts") }
                Text("An unexpected error occurred.")
            }
        }
    }
}

/** The distinguishable outcomes of a failed transcript load, so the UI can react to each. */
private sealed interface LoadError {
    data object Forbidden : LoadError
    data object NotFound : LoadError
    data object Unexpected : LoadError
}

/**
 * Maps a client exception to a [LoadError] off the typed HTTP status the generated client carries,
 * not the message text. Every 4xx surfaces as a [SmithyClientError] with an `httpStatusCode`, and a
 * 404 specifically as its [ChatNotFound] subtype; a 403 is the AVP gate refusing, the common case
 * worth a bespoke message. Reading the status directly means a rewording of a server error message can
 * never silently reclassify the outcome the way a `contains("404")` substring match could.
 */
private fun classifyError(e: Throwable): LoadError = when {
    e is ChatNotFound -> LoadError.NotFound
    e is SmithyClientError && e.httpStatusCode == 403 -> LoadError.Forbidden
    e is SmithyClientError && e.httpStatusCode == 404 -> LoadError.NotFound
    else -> LoadError.Unexpected
}

/** Trims an ISO-8601 instant to `YYYY-MM-DD HH:MM` for compact display; passes through anything else. */
private fun formatTimestamp(iso: String?): String {
    if (iso.isNullOrBlank()) return "—"
    val t = iso.indexOf('T')
    if (t < 0) return iso
    val date = iso.substring(0, t)
    val time = iso.substring(t + 1).take(5)
    return "$date $time"
}

/**
 * Best-effort pretty-print of a JSON string for tool arguments. The value arrives already serialized
 * (the server stringifies the arguments object), so this only re-indents; if parsing fails — the value
 * was not JSON — the original string is returned unchanged rather than throwing.
 */
private fun prettyJson(raw: String): String = try {
    val parsed = JSON.parse<Any?>(raw)
    JSON.stringify(parsed, null, 2)
} catch (e: Throwable) {
    raw
}
